import { Injectable, OnDestroy } from '@angular/core';

import { FotoVistoria } from '../models/item-vistoria-preenchimento';
import { VistoriaApiService } from './vistoria-api.service';

const LADO_MAXIMO = 2560;
const QUALIDADE_JPEG = 0.85;

/** Recorte quadrado da foto, em pixels da foto já com a orientação do EXIF aplicada. */
export interface RecorteFoto {
  x: number;
  y: number;
  lado: number;
}

/**
 * URLs locais (blob:) das fotos da tela, por chave. Fornecido pela tela `realizar-vistoria` (uma instância
 * por tela): as URLs são revogadas quando a tela é destruída. A foto pendente usa a URL do próprio Blob;
 * a salva é baixada pela API (o `<img src>` não manda o header Authorization) — e a que acabou de ser salva
 * continua com a URL do Blob, sem baixar de novo.
 */
@Injectable()
export class FotosVistoriaService implements OnDestroy {

  /** Seleções de fotos ainda em processamento (redução no aparelho). Enquanto > 0, a tela não deixa salvar:
   * a foto só entra no atributo ao fim do processamento, e um salvar no meio a perderia. */
  public processando = 0;

  private readonly miniaturas = new Map<string, string>();
  private readonly fotosCheias = new Map<string, string>();
  private readonly baixando = new Set<string>();

  constructor(private vistoriaApiService: VistoriaApiService) { }

  ngOnDestroy(): void {
    this.miniaturas.forEach(url => URL.revokeObjectURL(url));
    this.fotosCheias.forEach(url => URL.revokeObjectURL(url));
  }

  /** Recorta o quadrado e reduz a foto (no máximo 2560 px de lado, JPEG 85%) num passo só, codificando uma
   * vez — o createImageBitmap já aplica a orientação do EXIF, a mesma do cropper — e devolve a foto pendente,
   * pronta pra entrar no atributo. */
  public async criarFotoPendente(arquivo: File, recorte: RecorteFoto): Promise<FotoVistoria> {
    const bitmap = await createImageBitmap(arquivo, { imageOrientation: 'from-image' });
    const canvas = document.createElement('canvas');
    let blob: Blob;
    try {
      const lado = Math.min(recorte.lado, bitmap.width - recorte.x, bitmap.height - recorte.y);
      const ladoFinal = Math.min(lado, LADO_MAXIMO);

      canvas.width = ladoFinal;
      canvas.height = ladoFinal;
      canvas.getContext('2d')!.drawImage(bitmap, recorte.x, recorte.y, lado, lado, 0, 0, ladoFinal, ladoFinal);

      blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('Falha ao reduzir a foto')), 'image/jpeg', QUALIDADE_JPEG));
    } finally {
      bitmap.close();
      // Solta a memória do canvas na hora, sem esperar o coletor de lixo (a câmera do celular já pressiona a memória).
      canvas.width = 0;
      canvas.height = 0;
    }

    const chave = crypto.randomUUID();
    this.registrarFotoPendente(chave, blob);
    return { chave, dataCaptura: new Date(arquivo.lastModified), arquivo: blob };
  }

  /** Foto pendente (nova ou restaurada do rascunho local): miniatura e foto cheia usam a URL do próprio Blob. */
  public registrarFotoPendente(chave: string, blob: Blob): void {
    const url = URL.createObjectURL(blob);
    this.miniaturas.set(chave, url);
    this.fotosCheias.set(chave, url);
  }

  /** Solta a foto cheia baixada da API (ao fechar a foto ampliada). A da pendente (e a da que acabou de ser
   * salva) é a mesma URL da miniatura e continua. */
  public liberarFotoCheia(foto: FotoVistoria): void {
    const url = this.fotosCheias.get(foto.chave);
    if (!url || url === this.miniaturas.get(foto.chave)) return;

    URL.revokeObjectURL(url);
    this.fotosCheias.delete(foto.chave);
    this.baixando.delete(`cheia:${foto.chave}`);
  }

  /** URL da miniatura; foto salva ainda não baixada começa o download e devolve '' até ele terminar. */
  public miniatura(vistoriaId: number, foto: FotoVistoria): string {
    return this.urlOuBaixar(this.miniaturas, vistoriaId, foto, true);
  }

  /** URL da foto em tamanho cheio (ampliar); mesma lógica da miniatura. */
  public fotoCheia(vistoriaId: number, foto: FotoVistoria): string {
    return this.urlOuBaixar(this.fotosCheias, vistoriaId, foto, false);
  }

  private urlOuBaixar(cache: Map<string, string>, vistoriaId: number, foto: FotoVistoria, miniatura: boolean): string {
    const url = cache.get(foto.chave);
    if (url) return url;

    // Chamado a cada ciclo de detecção de mudanças: baixa uma vez só (nem repete após erro).
    const chaveDownload = `${miniatura ? 'mini' : 'cheia'}:${foto.chave}`;
    if (!foto.arquivo && vistoriaId > 0 && !this.baixando.has(chaveDownload)) {
      this.baixando.add(chaveDownload);
      this.vistoriaApiService.getFoto(vistoriaId, foto.chave, miniatura)
        .subscribe(blob => cache.set(foto.chave, URL.createObjectURL(blob)));
    }

    return '';
  }
}
